import type { CheckMonitorPeriod } from 'types/checkMonitor';

export const browserTimeZone = (): string | undefined => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) return undefined;
    new Intl.DateTimeFormat('en', { timeZone: zone }).format();
    return zone;
  } catch {
    return undefined;
  }
};

export const localDate = (instant: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) => parts.find((part) => part.type === type)!.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

// UTC is used only for civil calendar arithmetic, never to reconstruct API buckets.
const civil = (date: string) => new Date(`${date}T12:00:00Z`);
const isoDate = (date: Date) => date.toISOString().slice(0, 10);
export const dayOfMonth = (date: string) => Number(date.slice(8));
export const periodKey = (
  anchor: string,
  period: CheckMonitorPeriod
): string => {
  if (period === 'month') return anchor.slice(0, 7);
  if (period === 'day') return anchor;
  const date = civil(anchor);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
  return isoDate(date);
};
export const moveReference = (
  anchor: string,
  period: CheckMonitorPeriod,
  direction: number,
  preferredDay: number
): string => {
  const date = civil(anchor);
  if (period === 'month') {
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + direction);
    const lastDay = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)
    ).getUTCDate();
    date.setUTCDate(Math.min(preferredDay, lastDay));
  } else
    date.setUTCDate(
      date.getUTCDate() + direction * (period === 'week' ? 7 : 1)
    );
  return isoDate(date);
};

export const displayInstant = (
  instant: string,
  timeZone: string,
  time = false
) =>
  new Intl.DateTimeFormat('es-MX', {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(time
      ? {
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23' as const,
          timeZoneName: 'shortOffset' as const,
        }
      : {}),
  }).format(new Date(instant));

export const intervalLabel = (start: string, end: string, timeZone: string) =>
  `${displayInstant(start, timeZone, true)} – ${displayInstant(end, timeZone, true)}`;
